import {
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { faPaperPlaneTop } from '@fortawesome/pro-duotone-svg-icons/faPaperPlaneTop';
import { faFloppyDisk } from '@fortawesome/pro-duotone-svg-icons/faFloppyDisk';
import { faFaceSmile } from '@fortawesome/pro-duotone-svg-icons/faFaceSmile';
import { faCamera } from '@fortawesome/pro-duotone-svg-icons/faCamera';
import { faImagePortrait } from '@fortawesome/pro-duotone-svg-icons/faImagePortrait';
import { faTrashCan } from '@fortawesome/pro-duotone-svg-icons/faTrashCan';
import { faLock } from '@fortawesome/pro-duotone-svg-icons/faLock';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TooltipDirective } from '../../../../shared/components/ui/tooltip/tooltip.directive';
import { MatDialog } from '@angular/material/dialog';
import { TextField } from '../../../../shared/components/form-fields/text-field/text-field';
import { TextFieldAction } from '../../../../shared/components/form-fields/text-field/text-field.types';
import { User } from '../../../../state/user.repository';
import { UserService } from '../../../../core/services/user.service';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { EmojiPicker } from '../../../../shared/components/form-fields/emoji-picker/emoji-picker';
import { ContextMenu } from '../../../../shared/components/ui/context-menu/context-menu';
import { UserSettingsStore } from '../../../../state/user-settings.repository';
import { Fieldset } from '../../../../shared/components/ui/fieldset/fieldset';
import { MatToggleWrapper } from '../../../../shared/components/material-wrappers/mat-toggle-wrapper/mat-toggle-wrapper';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslatePipe } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { resolveAvatar } from '../../../../shared/helpers/avatar';
import { isAcceptableSource } from '../../../../shared/helpers/avatar-image';
import {
  AvatarCropperDialog,
  AvatarCropperData,
} from '../avatar-cropper-dialog/avatar-cropper-dialog';

@Component({
  selector: 'andor-settings-profile',
  standalone: true,
  imports: [
    CommonModule,
    FontAwesomeModule,
    TooltipDirective,
    ReactiveFormsModule,
    TranslatePipe,
    TextField,
    EmojiPicker,
    ContextMenu,
    Fieldset,
    MatToggleWrapper,
    MatSlideToggle,
  ],
  templateUrl: './settings-profile.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './settings-profile.scss',
})
export class SettingsProfile {
  private readonly _userService = inject(UserService);
  private readonly _dialog = inject(MatDialog);

  userNameControl = new FormControl('', [
    Validators.required,
    Validators.minLength(3),
    Validators.pattern(/^[A-Za-z].*$/),
  ]);
  statusMessageControl = new FormControl('', [Validators.maxLength(100)]);
  user?: User;

  readonly avatarUrl = computed(() => {
    const user = this._userService.ownUser();
    return user ? resolveAvatar(user.data.avatar, user.data.name) : '';
  });
  // Whether a custom avatar is set (vs. the name-derived default). Gates the
  // "remove" affordance.
  readonly hasCustomAvatar = computed(() => !!this._userService.ownUser()?.data.avatar?.trim());
  readonly currentStatus = computed(() => this._userService.ownUser()?.data.status ?? 'online');
  readonly isAway = computed(() => this.currentStatus() === 'away');

  protected readonly dragOver = signal(false);
  protected readonly uploadError = signal<string | null>(null);

  protected readonly faCamera = faCamera;
  protected readonly faImagePortrait = faImagePortrait;
  protected readonly faTrashCan = faTrashCan;
  protected readonly faLock = faLock;

  @ViewChild('emojiMenu') emojiMenu?: ContextMenu;
  @ViewChild('statusField') statusField?: TextField;
  @ViewChild('avatarInput') avatarInput?: ElementRef<HTMLInputElement>;

  saveDisplayNameAction: TextFieldAction = {
    icon: faFloppyDisk,
    tooltip: 'Save profile',
    ariaLabel: 'Save profile',
    click: () => {
      if (!this.userNameControl.valid) {
        this.userNameControl.markAsTouched();
        return { error: true, reason: 'Please fix the display name validation errors.' };
      }
      this.save();
      return { success: true, reason: 'Profile updated successfully.' };
    },
  };

  updateStatusAction: TextFieldAction = {
    icon: faPaperPlaneTop,
    tooltip: 'Save status',
    ariaLabel: 'Save status',
    click: (value) => {
      if (!this.statusMessageControl.valid) {
        this.statusMessageControl.markAsTouched();
        return { error: true, reason: 'Please fix the status message validation errors.' };
      }
      this.updateStatusMessage(value);
      return { success: true, reason: 'Status updated.' };
    },
  };

  statusMessageInlineActions: TextFieldAction[] = [
    {
      icon: faFaceSmile,
      tooltip: 'Pick an emoji',
      ariaLabel: 'Pick an emoji',
      click: (_value, event) => {
        if (!event) return;
        this.emojiMenu?.toggle(event.currentTarget as HTMLElement);
      },
    },
  ];

  constructor(
    public userService: UserService,
    public userSettingsStore: UserSettingsStore
  ) {
    this.user = this.userService.ownUser();
    if (this.user) {
      this.userNameControl.patchValue(this.user.data.name);
    }
  }

  // --- Avatar upload ---------------------------------------------------------

  protected openAvatarPicker(): void {
    this.uploadError.set(null);
    this.avatarInput?.nativeElement.click();
  }

  protected onAvatarInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Reset so picking the same file again still fires `change`.
    input.value = '';
    if (file) void this._cropAndApply(file);
  }

  protected onAvatarDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(true);
  }

  protected onAvatarDragLeave(): void {
    this.dragOver.set(false);
  }

  protected onAvatarDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) void this._cropAndApply(file);
  }

  // Stores the cropped avatar in user-settings only. The `avatar$` subscription
  // in `UserService.profileStateHandler` then mirrors it onto the own user and
  // flushes it to peers via ODIN userData — no extra wiring needed here.
  protected removeAvatar(): void {
    this.uploadError.set(null);
    this.userSettingsStore.update({ avatar: '' });
  }

  private async _cropAndApply(file: File): Promise<void> {
    if (!isAcceptableSource(file)) {
      this._setUploadError('Please choose an image file up to 12 MB.');
      return;
    }
    const ref = this._dialog.open(AvatarCropperDialog, {
      panelClass: 'avatar-cropper-dialog',
      backdropClass: 'avatar-cropper-backdrop',
      data: { file } satisfies AvatarCropperData,
    });
    const dataUrl = await firstValueFrom(ref.afterClosed());
    if (dataUrl) {
      this.userSettingsStore.update({ avatar: dataUrl });
    }
  }

  private _setUploadError(text: string): void {
    this.uploadError.set(text);
    setTimeout(() => this.uploadError.set(null), 5000);
  }

  toggleStatus() {
    this._userService.toggleStatus();
  }

  updateStatusMessage(text: string) {
    if (!this.user) return;
    this.userService.updateUserData({ statusMessage: text || undefined });
  }

  appendEmoji(emoji: string) {
    this.statusField?.insertAtCursor(emoji);
  }

  save() {
    if (!this.user) return;
    const userName = this.userNameControl.value;
    if (userName) {
      this.userSettingsStore.update({ userName });
    }
  }
}
